/**
 * IntakeFormTemplatesPage
 *
 * Lists clinic-authored intake form templates and lets staff create / edit /
 * duplicate / archive them. Edit opens the IntakeFormBuilder.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, Plus, Pencil, Copy, Archive, FileText } from 'lucide-react';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';
import {
  intakeFormTemplateService,
  type IntakeFormTemplate,
} from '@/services/intakeFormTemplateService';
import { IntakeFormBuilder } from '@/components/intake-builder/IntakeFormBuilder';

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; template: IntakeFormTemplate };

const IntakeFormTemplatesPage: React.FC = () => {
  const { clinic } = useClinic();
  const clinicId = clinic?.id;
  const { toast } = useToast();
  const [templates, setTemplates] = useState<IntakeFormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [archiveTarget, setArchiveTarget] = useState<IntakeFormTemplate | null>(null);

  const reload = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const list = await intakeFormTemplateService.list(clinicId, { active: true });
      setTemplates(list);
    } catch (err: any) {
      toast({ title: 'Failed to load templates', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [clinicId, toast]);

  useEffect(() => { reload(); }, [reload]);

  const handleCreate = async (payload: Parameters<typeof intakeFormTemplateService.create>[1]) => {
    if (!clinicId) return;
    setSaving(true);
    try {
      await intakeFormTemplateService.create(clinicId, payload);
      toast({ title: 'Form created' });
      setMode({ kind: 'list' });
      await reload();
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string, payload: Parameters<typeof intakeFormTemplateService.update>[2]) => {
    if (!clinicId) return;
    setSaving(true);
    try {
      await intakeFormTemplateService.update(clinicId, id, payload);
      toast({ title: 'Form saved' });
      setMode({ kind: 'list' });
      await reload();
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (t: IntakeFormTemplate) => {
    if (!clinicId) return;
    try {
      const copy = await intakeFormTemplateService.duplicate(clinicId, t.id);
      toast({ title: 'Form duplicated', description: copy.name });
      await reload();
    } catch (err: any) {
      toast({ title: 'Duplicate failed', description: err.message, variant: 'destructive' });
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget || !clinicId) return;
    try {
      await intakeFormTemplateService.archive(clinicId, archiveTarget.id);
      toast({ title: 'Form archived' });
      setArchiveTarget(null);
      await reload();
    } catch (err: any) {
      toast({ title: 'Archive failed', description: err.message, variant: 'destructive' });
    }
  };

  if (mode.kind === 'create') {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">New intake form</h1>
            <p className="text-sm text-slate-500">Build a custom form to send patients before their visit.</p>
          </div>
        </div>
        <IntakeFormBuilder
          saving={saving}
          onCancel={() => setMode({ kind: 'list' })}
          onSave={handleCreate}
        />
      </div>
    );
  }

  if (mode.kind === 'edit') {
    const t = mode.template;
    return (
      <div className="container mx-auto p-6 space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Edit “{t.name}”</h1>
          <p className="text-sm text-slate-500">Version {t.version} · {t.is_published ? 'Published' : 'Draft'}</p>
        </div>
        <IntakeFormBuilder
          initialName={t.name}
          initialDescription={t.description || ''}
          initialEstimatedMinutes={t.estimated_minutes}
          initialSchema={t.schema}
          initialIsPublished={t.is_published}
          saving={saving}
          onCancel={() => setMode({ kind: 'list' })}
          onSave={p => handleUpdate(t.id, p)}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Intake forms</h1>
          <p className="text-sm text-slate-500">
            Custom forms you can attach to appointment types or send ad-hoc.
          </p>
        </div>
        <Button onClick={() => setMode({ kind: 'create' })}>
          <Plus className="h-4 w-4 mr-1" /> New form
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : templates.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-700 font-medium">No custom forms yet</p>
          <p className="text-sm text-slate-500 mb-4">Build your first intake form to send to patients before visits.</p>
          <Button onClick={() => setMode({ kind: 'create' })}>
            <Plus className="h-4 w-4 mr-1" /> New form
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(t => (
            <Card key={t.id} className="p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900 truncate">{t.name}</h3>
                    {t.is_published ? (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Published</Badge>
                    ) : (
                      <Badge variant="outline">Draft</Badge>
                    )}
                    <Badge variant="outline" className="text-xs">v{t.version}</Badge>
                  </div>
                  {t.description && (
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{t.description}</p>
                  )}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                {t.schema?.sections?.length || 0} section
                {(t.schema?.sections?.length || 0) === 1 ? '' : 's'}
                {' · '}
                {(t.schema?.sections || []).reduce((acc, s) => acc + (s.questions?.length || 0), 0)} questions
                {t.estimated_minutes ? ` · ~${t.estimated_minutes} min` : ''}
              </div>
              <div className="flex flex-wrap gap-2 mt-auto">
                <Button variant="outline" size="sm" onClick={() => setMode({ kind: 'edit', template: t })}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDuplicate(t)}>
                  <Copy className="h-3 w-3 mr-1" /> Duplicate
                </Button>
                <Button variant="outline" size="sm" onClick={() => setArchiveTarget(t)}>
                  <Archive className="h-3 w-3 mr-1" /> Archive
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!archiveTarget} onOpenChange={open => !open && setArchiveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive “{archiveTarget?.name}”?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Archived forms are hidden from the booking selector but past responses
            still display correctly. You can't undo this from here.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmArchive}>Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntakeFormTemplatesPage;
